// src/components/ModalNovoCliente.jsx
import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrashAlt, FaCar, FaMotorcycle, FaTruck, FaSave } from 'react-icons/fa';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { getFirestore, collection, getDocs } from 'firebase/firestore';

const DEFAULT_FORM = {
  nome: '', telefone: '', email: '', documento: '', cpf: '', cnpj: '',
  cep: '', endereco: '', numero: '', complemento: '', bairro: '',
  cidade: '', estado: '', obs: '', codigoIBGE: ''
};

const DEFAULT_VEICULO = { tipo: 'Carro', placa: '', marca: '', modelo: '', ano: '', cor: '' };

const ModalNovoCliente = ({
  open,
  onClose,
  onSave,
  initialData,
  editando,
  empresaId,
  clienteId,
}) => {
  const [form, setForm] = useState(initialData || DEFAULT_FORM);
  const [veiculos, setVeiculos] = useState([]);
  const [novoVeiculo, setNovoVeiculo] = useState(DEFAULT_VEICULO);
  const [carregandoCep, setCarregandoCep] = useState(false);
  const [carregandoVeiculos, setCarregandoVeiculos] = useState(false);

  useEffect(() => {
    setForm({ ...DEFAULT_FORM, ...(initialData || {}) });
    setVeiculos([]);
    setNovoVeiculo(DEFAULT_VEICULO);
  }, [initialData, open]);

  useEffect(() => {
    const canLoad = open && editando && empresaId && clienteId;
    if (!canLoad) return;

    (async () => {
      try {
        setCarregandoVeiculos(true);
        const db = getFirestore();
        const snap = await getDocs(
          collection(db, 'empresas', empresaId, 'clientes', clienteId, 'veiculos')
        );
        const lista = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setVeiculos(lista);
      } catch (e) {
        console.error('Erro ao carregar veículos do cliente:', e);
      } finally {
        setCarregandoVeiculos(false);
      }
    })();
  }, [open, editando, empresaId, clienteId]);

  useEffect(() => {
    const buscarEndereco = async () => {
      const cepLimpo = (form?.cep || '').replace(/\D/g, '');
      if (cepLimpo.length !== 8) return;

      setCarregandoCep(true);
      try {
        const res = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
        const data = await res.json();
        if (!data.erro) {
          setForm(f => ({
            ...f,
            endereco: data.logradouro || f.endereco,
            bairro: data.bairro || f.bairro,
            cidade: data.localidade || f.cidade,
            estado: data.uf || f.estado,
            codigoIBGE: data.ibge || f.codigoIBGE
          }));
        }
      } catch (err) {
        console.error('Erro ao buscar CEP:', err);
      } finally {
        setCarregandoCep(false);
      }
    };

    if (open) buscarEndereco();
  }, [open, form?.cep]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAdicionarVeiculo = () => {
    if (!novoVeiculo.placa || !novoVeiculo.marca || !novoVeiculo.modelo) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos obrigatórios',
        text: 'Preencha pelo menos a placa, marca e modelo do veículo.',
        confirmButtonColor: '#2563eb',
      });
      return;
    }
    setVeiculos(prev => [...prev, { ...novoVeiculo, placa: novoVeiculo.placa.toUpperCase() }]);
    setNovoVeiculo(DEFAULT_VEICULO);
  };

  const handleRemoverVeiculo = (index) => {
    setVeiculos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSalvarCliente = () => {
    if (!form.nome || !form.telefone) {
      Swal.fire({
        icon: 'warning',
        title: 'Dados obrigatórios',
        text: 'Informe o Nome e o Telefone do cliente.',
        confirmButtonColor: '#2563eb',
      });
      return;
    }
    onSave({ formData: form, veiculos });
  };

  const closeOnBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  // ==== helper para escolher o ícone pelo tipo ====
  const getIconByTipo = (tipo) => {
    if (tipo === 'Moto') {
      return <FaMotorcycle className="text-orange-500 shrink-0" />;
    }
    if (tipo === 'Caminhão') {
      return <FaTruck className="text-blue-500 shrink-0" />;
    }
    // default: Carro (ou qualquer outro valor)
    return <FaCar className="text-blue-500 shrink-0" />;
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-[2px] animate-fadein"
      onClick={closeOnBackdrop}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-4xl relative animate-modalSlideIn overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="max-h-[94vh] flex flex-col">
          {/* Header */}
          <div className="p-7 pb-0 relative">
            <button
              className="absolute right-8 top-7 text-gray-500 hover:text-blue-700 dark:text-gray-200 dark:hover:text-white text-base font-medium"
              onClick={onClose}
            >
              Fechar
            </button>
            <h2 className="text-2xl font-bold mb-6 text-blue-600 dark:text-white">
              {editando ? "Editar Cliente" : "Novo Cliente"}
            </h2>
          </div>

          {/* Body */}
          <div className="px-7 pb-6 space-y-6 overflow-y-auto flex-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <InputField label="Nome" name="nome" value={form.nome} onChange={handleChange} placeholder="Ex: João da Silva" required />
              <InputField label="Telefone" name="telefone" value={form.telefone} onChange={handleChange} placeholder="(16) 99999-9999" required />
              <InputField label="Email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="Ex: cliente@email.com" />
              <InputField label="CPF/CNPJ" name="documento" value={form.documento} onChange={handleChange} placeholder="Ex: 123.456.789-00" />
            </div>

            <fieldset className="border-t pt-6">
              <legend className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Endereço</legend>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                <div className="md:col-span-1">
                  <label className="block text-sm font-semibold mb-1 dark:text-gray-200">CEP</label>
                  <div className="flex gap-2">
                    <input
                      name="cep"
                      className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD]"
                      value={form.cep}
                      onChange={handleChange}
                      placeholder="00000-000"
                    />
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, cep: (f.cep || '').replace(/\D/g, '').slice(0, 8) }))}
                      className="px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                      title="Normalizar CEP (somente números)"
                    >
                      CEP
                    </button>
                  </div>
                  {carregandoCep && <p className="text-xs text-gray-500 mt-1">Buscando endereço…</p>}
                </div>

                <div className="md:col-span-3">
                  <InputField label="Logradouro" name="endereco" value={form.endereco} onChange={handleChange} placeholder="Rua, avenida..." />
                </div>

                <InputField label="Número" name="numero" value={form.numero} onChange={handleChange} placeholder="Ex: 123" />
                <InputField label="Complemento" name="complemento" value={form.complemento} onChange={handleChange} placeholder="Apto, bloco..." className="md:col-span-2" />
                <InputField label="Bairro" name="bairro" value={form.bairro} onChange={handleChange} placeholder="Centro" />
                <InputField label="Cidade" name="cidade" value={form.cidade} onChange={handleChange} placeholder="São Paulo" />
                <InputField label="Estado (UF)" name="estado" value={form.estado} onChange={(e)=> setForm(f=>({...f, estado: e.target.value.toUpperCase().slice(0,2)}))} placeholder="SP" />
              </div>
            </fieldset>

            <fieldset className="border-t pt-6">
              <legend className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Veículos</legend>

              {carregandoVeiculos ? (
                <div className="text-sm text-gray-500 mb-2">Carregando veículos...</div>
              ) : veiculos.length > 0 ? (
                <div className="space-y-2 mb-4">
                  {veiculos.map((v, index) => (
                    <div key={index} className="bg-gray-50 dark:bg-gray-800/70 px-4 py-3 rounded-xl flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        {getIconByTipo(v.tipo)}
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {v.marca} {v.modelo}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Placa: {v.placa} {v.cor ? `| Cor: ${v.cor}` : ''} {v.ano ? `| Ano: ${v.ano}` : ''}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoverVeiculo(index)}
                        className="p-2 text-red-600 hover:text-red-700 rounded-md"
                        title="Remover"
                      >
                        <FaTrashAlt size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 mb-2">Nenhum veículo adicionado.</div>
              )}

              <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl grid grid-cols-2 md:grid-cols-3 gap-4">
                <SelectField
                  label="Tipo"
                  name="tipo"
                  value={novoVeiculo.tipo}
                  onChange={(e) => setNovoVeiculo(v => ({ ...v, tipo: e.target.value }))}
                  options={['Carro', 'Moto', 'Caminhão']}
                />
                <InputField
                  label="Placa"
                  name="placa"
                  value={novoVeiculo.placa}
                  onChange={(e) => setNovoVeiculo(v => ({ ...v, placa: e.target.value.toUpperCase() }))}
                  placeholder="ABC-1234"
                />
                <InputField label="Marca" name="marca" value={novoVeiculo.marca} onChange={(e) => setNovoVeiculo(v => ({ ...v, marca: e.target.value }))} placeholder="Ex: Fiat" />
                <InputField label="Modelo" name="modelo" value={novoVeiculo.modelo} onChange={(e) => setNovoVeiculo(v => ({ ...v, modelo: e.target.value }))} placeholder="Ex: Uno" />
                <InputField label="Ano" name="ano" type="number" value={novoVeiculo.ano} onChange={(e) => setNovoVeiculo(v => ({ ...v, ano: e.target.value }))} placeholder="Ex: 2020" />
                <InputField label="Cor" name="cor" value={novoVeiculo.cor} onChange={(e) => setNovoVeiculo(v => ({ ...v, cor: e.target.value }))} placeholder="Ex: Prata" />

                <div className="col-span-2 md:col-span-3">
                  <button
                    type="button"
                    onClick={handleAdicionarVeiculo}
                    className="w-full mt-2 bg-blue-100 text-blue-900 dark:bg-blue-900/50 dark:text-blue-200 font-semibold py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 hover:bg-blue-200 dark:hover:bg-blue-800 transition"
                  >
                    <FaPlus size={12} /> Adicionar Veículo
                  </button>
                </div>
              </div>
            </fieldset>
          </div>

          {/* Separador + botão (como no padrão com “linha” superior) */}
          <div className="px-7 py-5 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-t border-gray-200 dark:border-gray-800">
            <button
              onClick={handleSalvarCliente}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3.5 font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition"
            >
              <FaSave /> {editando ? 'Salvar Alterações' : 'Cadastrar Cliente'}
            </button>
          </div>
        </div>

        <style>{`
          .animate-modalSlideIn { animation: modalSlideIn 0.35s cubic-bezier(.32,.72,.57,1) both; }
          @keyframes modalSlideIn {
            0% { opacity:0; transform: translateY(60px) scale(.97); }
            100% { opacity:1; transform: translateY(0) scale(1); }
          }
          .animate-fadein { animation: fadein 0.25s; }
          @keyframes fadein { from { opacity: 0; } to { opacity: 1; } }
        `}</style>
      </div>
    </div>
  );
};

const InputField = ({ label, name, value, onChange, loading, className = '', ...props }) => (
  <div className={className}>
    <label htmlFor={name} className="block text-sm font-semibold mb-1 dark:text-gray-200">{label}</label>
    <div className="relative">
      <input
        id={name}
        name={name}
        value={value || ''}
        onChange={onChange}
        {...props}
        className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD] transition"
      />
      {loading && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">...</span>}
    </div>
  </div>
);

const SelectField = ({ label, name, value, onChange, options, className = '' }) => (
  <div className={className}>
    <label htmlFor={name} className="block text-sm font-semibold mb-1 dark:text-gray-200">{label}</label>
    <select
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD] transition"
    >
      {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
    </select>
  </div>
);

export default ModalNovoCliente;
